import React, { useRef } from 'react';
import styled from '@emotion/styled';
import { Flex, Input, IconButton, Palette } from '@updater/ui-uds';
import { PaperPlaneTilt, Plus } from '@phosphor-icons/react';

const Container = styled(Flex)`
  padding: 1rem 1.5rem 1.5rem 1.5rem;
  gap: 0.75rem;
  border-radius: 0 0 12px 12px;
  align-items: stretch;
  flex-shrink: 0;
  position: relative;
`;

const InputButton = styled(IconButton)<{ hasText: boolean }>`
  background-color: ${({ hasText }: { hasText: boolean }) =>
    hasText ? '#006eb3' : 'none'};
  background-image: ${({ hasText }: { hasText: boolean }) =>
    hasText
      ? 'none'
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.1) 100%)'};
  border-color: ${({ hasText }: { hasText: boolean }) =>
    hasText ? '#006eb3' : 'rgba(255, 255, 255, 0.1)'};
  &:hover:not(:disabled) {
    background-color: #009cfc !important;
  }
  &:disabled {
    background-color: transparent !important;
    background-image: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.1) 100%
    ) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;

    &:hover {
      background-color: transparent !important;
      background-image: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.1) 100%
      ) !important;
      border-color: rgba(255, 255, 255, 0.1) !important;
    }
  }
`;

const InputWrapper = styled(Flex)`
  flex: 1 !important;
  min-width: 0 !important;
  max-width: none !important;
  display: flex !important;
  align-items: center !important;

  /* Style the UDS Input root wrapper rendered inside */
  > div:first-of-type {
    flex: 1 !important;
    min-width: 0 !important;
    max-width: none !important;
    display: flex !important;
    background-color: rgba(255, 255, 255, 0.08) !important;
    border: 0 !important;
    border-radius: 24px !important;
    height: 45px !important;
    align-items: center !important;
    justify-content: center !important;
    &:focus-within {
      border-color: #009cfc !important;
      box-shadow: none !important;
    }
    &:hover {
      border-color: #009cfc !important;
    }

    input {
      min-width: 0 !important;
      flex: 1 !important;
      width: 100% !important;
      max-width: none !important;
      box-sizing: border-box !important;
      color: white !important;
      font-size: 1rem !important;
      padding: 0 1.5rem !important;
      background-color: transparent !important;
      text-align: left !important;
      &:focus {
        color: white !important;
        text-align: left !important;
      }
      &[type='text'] {
        color: white !important;
        text-align: left !important;
      }
      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus,
      &:-webkit-autofill:active {
        -webkit-text-fill-color: white !important;
        -webkit-box-shadow: 0 0 0px 1000px rgba(255, 255, 255, 0.1) inset !important;
        background-color: rgba(255, 255, 255, 0.1) !important;
      }
      &::placeholder {
        white-space: nowrap !important;
        overflow: visible !important;
        text-overflow: unset !important;
        opacity: 1 !important;
        color: rgba(255, 255, 255, 0.64) !important;
        display: block !important;
        width: auto !important;
        text-align: left !important;
      }
    }
  }
`;

// Inline file button that never shows a hover/disabled background fill
const InlineFileButton = styled(IconButton)`
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
  &:hover {
    background-color: transparent !important;
    box-shadow: none !important;
  }
  &:active {
    background-color: transparent !important;
    box-shadow: none !important;
  }
  &:focus {
    background-color: transparent !important;
    box-shadow: none !important;
  }
  &:disabled {
    background-color: transparent !important;
    box-shadow: none !important;
  }
`;

type Props = {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onFileSelect?: (files: File[]) => void;
  placeholder: string;
  disabled?: boolean;
  onOpenVoiceAgent?: () => void;
};

export const InputBar: React.FC<Props> = ({
  value,
  onChange,
  onSend,
  onFileSelect,
  placeholder,
  disabled,
  onOpenVoiceAgent,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    if (files.length > 0 && onFileSelect) {
      onFileSelect(files);
    }
    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const hasText = Boolean(value.trim());

  return (
    <Container role="form" aria-label="Send message">
      <InputWrapper>
        <Input
          label=""
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange(e.target.value)
          }
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          data-testid="message-input"
          data-cy="message-input"
          aria-label="Type your message"
          rightSlot={
            <InlineFileButton
              onClick={handleFileButtonClick}
              disabled={disabled}
              data-testid="file-button"
              data-cy="file-button"
              variant="transparent"
              aria-label="Attach file"
              icon={<Plus size={18} color={Palette.white} />}
            />
          }
        />
      </InputWrapper>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        data-testid="file-input"
        data-cy="file-input"
      />

      <InputButton
        hasText={hasText}
        variant="transparent"
        onClick={hasText ? onSend : onOpenVoiceAgent}
        disabled={disabled || (!hasText && !onOpenVoiceAgent)}
        data-testid={hasText ? 'send-button' : 'microphone-button'}
        data-cy={hasText ? 'send-button' : 'microphone-button'}
        aria-label={hasText ? 'Send message' : 'Open voice agent'}
        icon={<PaperPlaneTilt size={18} color="white" />}
      />
    </Container>
  );
};

export default InputBar;
